import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

import {
  CerfaID,
  IUserCerfaHasAccess,
  IUserCerfasBySeason,
  IUserCerfasByShortId,
} from './user-cerfa.interface';
import { ApiResourceService } from '../api-resource/api-resource.service';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable()
export class UserCerfaResourceService {
  private readonly http = inject(HttpClient);
  private readonly apiResource = inject(ApiResourceService);
  private readonly authService = inject(AuthenticationService);

  public getCerfas(): Observable<IUserCerfasBySeason[]> {
    return this.authService.getToken().pipe(
      take(1),
      mergeMap((token) =>
        this.http.get<IUserCerfasBySeason[]>(
          this.apiResource.getApiUrl('/cerfas'),
          {
            headers: this.apiResource.getAuthorizationHeader(token),
          },
        ),
      ),
    );
  }

  public hasAccess(): Observable<IUserCerfaHasAccess> {
    return this.authService.getToken().pipe(
      take(1),
      mergeMap((token) =>
        this.http.get<IUserCerfaHasAccess>(
          this.apiResource.getApiUrl('/cerfas/has-access'),
          {
            headers: this.apiResource.getAuthorizationHeader(token),
          },
        ),
      ),
    );
  }

  public downloadCerfa(cerfaId: CerfaID): Observable<Blob> {
    return this.authService.getToken().pipe(
      take(1),
      mergeMap((token) =>
        this.http.get(
          this.apiResource.getApiUrl(`/cerfas/${cerfaId}/download`),
          {
            headers: this.apiResource.getAuthorizationHeader(token),
            responseType: 'blob',
          },
        ),
      ),
    );
  }

  /**
   * Get CERFAs by donor short_id (public endpoint, no auth required).
   * Returns CERFAs grouped by season, donor info, and a fresh auth token.
   */
  public getByShortId(shortId: string): Observable<IUserCerfasByShortId> {
    return this.http.get<IUserCerfasByShortId>(
      this.apiResource.getApiUrl(`/cerfas/by-donor/${shortId}`),
    );
  }
}
